import { baseApi } from '../../../core/baseApi';
import { createGetByIdQuery, createUpdateMutation, unwrapApiResponse } from '../../../patterns/crud';
import { ConfigureHawksoftParams, ConfigureHawksoftResponse } from '../types';

// Response type for logging messages
export interface LogHawksoftMessagesResponse {
  successful: number;
  failed: number;
  total: number;
}

// Task input for creating a follow-up task in HawkSoft
export interface HawksoftTaskInput {
  title: string;
  description: string;
  assignedToRole: 'CSR' | 'Producer' | 'Agent1' | 'Agent2' | 'Agent3' | 'SpecifiedUser';
  dueDate: string;
  assignedToEmail?: string;
  category?: string;
}

// Request params for logging messages
export interface LogHawksoftMessagesParams {
  messageIds?: string[];
  conversationId?: string;
  clientId?: number;
  logType?: string;
  title?: string;
  task?: HawksoftTaskInput;
  closeAfterLog?: boolean;
}

// Request params for logging an attachment
export interface LogHawksoftAttachmentParams {
  conversationId: string;
  description: string;
  logNote: string;
  file: File;
}

// Sync conflict types
export interface SyncConflictContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  avatarColor?: string;
  source: string;
  externalId?: string;
}

export interface SyncConflictIncomingData {
  externalId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber: string;
  companyName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  metadata?: Record<string, unknown>;
}

export interface SyncConflict {
  id: string;
  organizationId: string;
  integrationId: string;
  existingContactId: string;
  existingContact?: SyncConflictContact;
  incomingContactData: SyncConflictIncomingData;
  matchType: 'phone_number';
  matchedPhoneNumber: string;
  status: 'pending' | 'resolved_merge' | 'resolved_create' | 'dismissed';
  source: 'hawksoft';
  resolvedByUserId?: string;
  resolvedAt?: string;
  resolvedContactId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SyncConflictsResponse {
  conflicts: SyncConflict[];
  count: number;
}

export interface SyncConflictCountResponse {
  count: number;
}

export interface ResolveConflictResponse {
  message: string;
  contact?: SyncConflictContact;
}

export interface HawksoftHealthResponse {
  status: 'active' | 'error' | 'inactive' | 'disconnected';
  message: string;
  lastCheckedAt?: string;
  agencyId?: string;
  environment?: string;
}

export const hawksoftApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHawksoftStatus: createGetByIdQuery('/integrations/hawksoft/status', 'HawksoftStatus')(builder),
    getHawksoftHealth: builder.query<HawksoftHealthResponse, void>({
      query: () => '/integrations/hawksoft/health',
      transformResponse: unwrapApiResponse,
      providesTags: ['HawksoftStatus'],
    }),
    disconnectHawksoft: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/integrations/hawksoft/disconnect',
        method: 'POST',
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['HawksoftStatus', 'IntegrationsStatus', 'Integrations'],
    }),
    configureHawksoft: builder.mutation<ConfigureHawksoftResponse, ConfigureHawksoftParams>({
      query: (args) => ({
        url: '/integrations/hawksoft/configure',
        method: 'POST',
        body: 'data' in args ? args.data : args
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['HawksoftStatus', 'IntegrationsStatus', 'Integrations']
    }),
    syncHawksoftData: builder.mutation<{ jobId: string; message: string }, { fullSync: boolean }>({
      query: (data) => ({
        url: '/integrations/hawksoft/sync',
        method: 'POST',
        body: data,
      }),
      transformResponse: unwrapApiResponse,
    }),

    getContactSyncStatus: builder.query<{
      state: string;
      progress: number;
      result?: {
        success: boolean;
        added: number;
        updated: number;
        unchanged: number;
        skipped: number;
        conflicts: number;
        errors: number;
        total: number;
        completedAt: string;
        processingTimeMs: number;
      };
      failedReason?: string;
    }, string>({
      query: (jobId) => `/integrations/hawksoft/sync-status/${jobId}`,
      transformResponse: unwrapApiResponse,
    }),
    updateHawksoftConfig: createUpdateMutation('/integrations/hawksoft', 'HawksoftStatus')(builder),
    logHawksoftMessages: builder.mutation<LogHawksoftMessagesResponse, LogHawksoftMessagesParams>({
      query: (data) => ({
        url: '/integrations/hawksoft/log-messages',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse,
      // Invalidate messages cache so UI refreshes with logged status
      invalidatesTags: (result, error, arg) => [
        { type: 'Messages', id: arg.conversationId },
        { type: 'Messages', id: 'LOGGING_EVENTS' },
      ]
    }),
    logHawksoftAttachment: builder.mutation<{ message: string }, LogHawksoftAttachmentParams>({
      query: (data) => {
        const formData = new FormData();
        formData.append('file', data.file);
        formData.append('conversationId', data.conversationId);
        formData.append('description', data.description);
        formData.append('logNote', data.logNote);
        return {
          url: '/integrations/hawksoft/log-attachment',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: unwrapApiResponse,
    }),
    logHawksoftConversation: builder.mutation<unknown, { conversationId: string }>({
      query: ({ conversationId }) => ({
        url: '/integrations/hawksoft/log-conversation',
        method: 'POST',
        body: { conversationId }
      }),
      transformResponse: unwrapApiResponse,
      // Use the same tag format as getMessagesForConversation (just conversationId, not prefixed)
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Messages', id: conversationId },
        { type: 'Messages', id: 'LOGGING_EVENTS' },
      ]
    }),
    retryHawksoftLogEntry: builder.mutation<{ successful: number; failed: number; total: number }, { logEntryId: string }>({
      query: (data) => ({
        url: '/integrations/hawksoft/retry-log-entry',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse,
      // Only invalidate logging events view, not all Messages queries
      invalidatesTags: [{ type: 'Messages', id: 'LOGGING_EVENTS' }]
    }),
    changeHawksoftClientId: builder.mutation<unknown, { conversationId: string; clientId: number; clientName?: string; clientPhone?: string }>({
      query: (data) => ({
        url: '/integrations/hawksoft/change-client-id',
        method: 'POST',
        body: data
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Conversations' as const, id: conversationId },
        { type: 'Conversations' as const, id: 'LIST' },
        { type: 'Conversations' as const, id: 'INFINITE-LIST' }
      ]
    }),
    getAutoLogConfig: builder.query<{
      autoLogEnabled: boolean;
      autoLogTime: string;
      autoLogDaysOfWeek: number[];
      logOnClose: boolean;
      logMissedEndOfDay: boolean;
      logType?: string;
    }, void>({
      query: () => '/integrations/hawksoft/auto-log-config',
      transformResponse: unwrapApiResponse,
      providesTags: ['HawksoftStatus']
    }),
    updateAutoLogConfig: builder.mutation<{
      message: string;
      config: {
        autoLogEnabled: boolean;
        autoLogTime: string;
        autoLogDaysOfWeek: number[];
        logOnClose: boolean;
        logMissedEndOfDay: boolean;
        logType: string;
      };
    }, {
      autoLogEnabled?: boolean;
      autoLogTime?: string;
      autoLogDaysOfWeek?: number[];
      logOnClose?: boolean;
      logMissedEndOfDay?: boolean;
      logType?: string;
    }>({
      query: (data) => ({
        url: '/integrations/hawksoft/auto-log-config',
        method: 'PUT',
        body: data
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['HawksoftStatus']
    }),

    // Auto-sync contact configuration
    getAutoSyncConfig: builder.query<{
      autoSyncEnabled: boolean;
      autoSyncTime: string;
      lastSyncAt: string | null;
    }, void>({
      query: () => '/integrations/hawksoft/auto-sync-config',
      transformResponse: unwrapApiResponse,
      providesTags: ['HawksoftStatus']
    }),

    updateAutoSyncConfig: builder.mutation<{
      message: string;
      config: {
        autoSyncEnabled: boolean;
        autoSyncTime: string;
        lastSyncAt: string | null;
      };
    }, {
      autoSyncEnabled?: boolean;
      autoSyncTime?: string;
    }>({
      query: (data) => ({
        url: '/integrations/hawksoft/auto-sync-config',
        method: 'PUT',
        body: data
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['HawksoftStatus']
    }),

    // Get all pending sync conflicts
    getSyncConflicts: builder.query<SyncConflictsResponse, void>({
      query: () => '/integrations/hawksoft/conflicts',
      transformResponse: unwrapApiResponse,
      providesTags: ['SyncConflicts']
    }),

    // Get sync conflict count (for badge display)
    getSyncConflictCount: builder.query<SyncConflictCountResponse, void>({
      query: () => '/integrations/hawksoft/conflicts/count',
      transformResponse: unwrapApiResponse,
      providesTags: ['SyncConflicts']
    }),

    // Get a single sync conflict by ID
    getSyncConflictById: builder.query<SyncConflict, string>({
      query: (id) => `/integrations/hawksoft/conflicts/${id}`,
      transformResponse: unwrapApiResponse,
      providesTags: (result, error, id) => [{ type: 'SyncConflicts', id }]
    }),

    // Resolve conflict by merging (same person)
    mergeSyncConflict: builder.mutation<ResolveConflictResponse, { conflictId: string }>({
      query: ({ conflictId }) => ({
        url: `/integrations/hawksoft/conflicts/${conflictId}/merge`,
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: (result) => [
        'SyncConflicts',
        ...(result?.contact?.id ? [{ type: 'Contacts' as const, id: result.contact.id }] : []),
        { type: 'Contacts' as const, id: 'LIST' },
        { type: 'Contacts' as const, id: 'PAGINATED-LIST' },
        { type: 'Contacts' as const, id: 'INFINITE-LIST' },
      ]
    }),

    // Resolve conflict by creating new contact (different people)
    createFromSyncConflict: builder.mutation<ResolveConflictResponse, string>({
      query: (conflictId) => ({
        url: `/integrations/hawksoft/conflicts/${conflictId}/create`,
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse,
      // For create, we need to invalidate the list to show the new contact
      invalidatesTags: () => [
        'SyncConflicts',
        { type: 'Contacts', id: 'LIST' },
        { type: 'Contacts', id: 'PAGINATED-LIST' },
        { type: 'Contacts', id: 'INFINITE-LIST' },
        'ContactsCount'
      ]
    }),

    // Dismiss conflict (skip/ignore)
    dismissSyncConflict: builder.mutation<{ message: string }, string>({
      query: (conflictId) => ({
        url: `/integrations/hawksoft/conflicts/${conflictId}/dismiss`,
        method: 'POST'
      }),
      transformResponse: unwrapApiResponse,
      invalidatesTags: ['SyncConflicts']
    }),
  })
});

export const {
  useGetHawksoftStatusQuery,
  useGetHawksoftHealthQuery,
  useDisconnectHawksoftMutation,
  useConfigureHawksoftMutation,
  useSyncHawksoftDataMutation,
  useLazyGetContactSyncStatusQuery,
  useUpdateHawksoftConfigMutation,
  useLogHawksoftMessagesMutation,
  useLogHawksoftAttachmentMutation,
  useLogHawksoftConversationMutation,
  useRetryHawksoftLogEntryMutation,
  useChangeHawksoftClientIdMutation,
  useGetAutoLogConfigQuery,
  useUpdateAutoLogConfigMutation,
  useGetAutoSyncConfigQuery,
  useUpdateAutoSyncConfigMutation,
  // Sync conflict hooks
  useGetSyncConflictsQuery,
  useGetSyncConflictCountQuery,
  useGetSyncConflictByIdQuery,
  useMergeSyncConflictMutation,
  useCreateFromSyncConflictMutation,
  useDismissSyncConflictMutation,
} = hawksoftApi;